import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { isElloPwaDomain } from '../../utils/elloDomains';
import { useAuthContext } from '../../contexts/AuthContext';
import './styles.css';

/** Segurança ÉLLO em domínio próprio. Não reutiliza logomarca nem fluxos visuais do I9. */
export default function BelezaAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthContext();
  const isPwa = isElloPwaDomain();
  const loginPath = isPwa ? '/pwa/colaborador' : '/login';
  const params = new URLSearchParams(location.search);
  const urlEmail = params.get('email') || '';
  const [email, setEmail] = useState(location.state?.email || urlEmail);
  const [code, setCode] = useState(location.state?.code || params.get('code') || '');
  const [senhaAtual, setSenhaAtual] = useState(location.state?.senhaAtual || sessionStorage.getItem('senhaAtual') || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const route = location.pathname.toLowerCase();
  const forgot = route.endsWith('/recuperar-senha');
  const validate = route.endsWith('/validate-reset-code');
  const reset = route.endsWith('/reset-password');
  const first = route.endsWith('/alterar-senha-primeiro-acesso');

  const title = forgot ? 'Recuperar palavra-passe' : validate ? 'Validar código de segurança'
    : reset ? 'Criar nova palavra-passe' : 'Ativar o seu acesso';
  const subtitle = forgot ? 'Enviaremos um código de segurança para o seu e-mail.'
    : validate ? 'Introduza o código enviado ao seu e-mail.'
    : reset ? 'Defina uma nova palavra-passe para a sua conta.'
    : 'Por segurança, altere a palavra-passe provisória antes de continuar.';
  useEffect(() => {
    const before = document.title;
    document.title = `${title} | ÉLLO Professional`;
    return () => { document.title = before; };
  }, [title]);

  const submit = async event => {
    event.preventDefault();
    if (working) return;
    setError(''); setMessage(''); setWorking(true);
    const normalizedEmail = email.trim().toLowerCase();
    try {
      if (forgot) {
        await api.post('/api/Usuario/forgot-password', { email: normalizedEmail });
        navigate(`/Security/validate-reset-code?email=${encodeURIComponent(normalizedEmail)}`, { state: { email: normalizedEmail } });
        return;
      }
      if (validate) {
        await api.post('/api/Usuario/validate-reset-code', { email: normalizedEmail, code: code.trim() });
        navigate('/Security/reset-password', { state: { email: normalizedEmail, code: code.trim() } });
        return;
      }
      if (!newPassword || newPassword !== confirmPassword) {
        throw new Error('Confirme a palavra-passe. Os dois campos devem ser iguais.');
      }
      if (newPassword.length < (first ? 6 : 10)) {
        throw new Error(`A palavra-passe deve ter pelo menos ${first ? 6 : 10} caracteres.`);
      }
      if (first) {
        if (!senhaAtual) throw new Error('É necessário informar a palavra-passe provisória.');
        await api.post('/api/Usuario/alterar-senha-primeiro-acesso', {
          email: normalizedEmail, senhaAtual, novaSenha: newPassword,
        });
      } else if (reset) {
        if (!code.trim()) throw new Error('Reabra o link de validação e informe o código recebido.');
        await api.post('/api/Usuario/reset-password', {
          email: normalizedEmail, code: code.trim(), newPassword, confirmPassword,
        });
      }
      // Limpa também o token em memória do Axios e o contexto React, sem
      // interromper a mensagem de confirmação da rota pública de segurança.
      logout();
      setMessage('Palavra-passe atualizada. Entre novamente com os novos dados.');
      setTimeout(() => { window.location.replace(loginPath); }, 1100);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Não foi possível concluir a operação.');
    } finally { setWorking(false); }
  };

  return (
    <div className={`ello-security ${isPwa ? 'ello-security-pro' : ''}`}>
      <aside className="ello-security-brand">
        <img src="/pwa-beleza-logo-escuro.jpg" alt="ÉLLO Professional" />
        <p className="ello-security-kicker">{isPwa ? 'APP DO PROFISSIONAL' : 'GESTÃO DE BELEZA E ESTÉTICA'}</p>
        <h1>O seu espaço, com segurança e autonomia.</h1>
        <p>A sua conta, a sua agenda e os seus resultados — sempre no ambiente ÉLLO.</p>
      </aside>
      <main className="ello-security-main">
        <section className="ello-security-card" aria-labelledby="ello-security-title">
          <img className="ello-security-mobile-logo" src="/pwa-beleza-logo-claro.jpg" alt="ÉLLO Professional" />
          <p className="ello-security-kicker">ACESSO SEGURO</p>
          <h2 id="ello-security-title">{title}</h2>
          <p className="ello-security-help">{subtitle}</p>
          {error && <p className="ello-security-alert" role="alert">{error}</p>}
          {message && <p className="ello-security-success" role="status">{message}</p>}
          <form onSubmit={submit}>
            <label htmlFor="ello-security-email">E-mail</label>
            <input id="ello-security-email" type="email" autoComplete="email" required value={email}
              onChange={e => setEmail(e.target.value)} />
            {(validate || reset) && <>
              <label htmlFor="ello-security-code">Código recebido</label>
              <input id="ello-security-code" type="text" required inputMode="numeric" autoComplete="one-time-code"
                maxLength={8} value={code} onChange={e => setCode(e.target.value)} />
            </>}
            {first && <>
              <label htmlFor="ello-security-current">Palavra-passe provisória</label>
              <input id="ello-security-current" required type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                value={senhaAtual} onChange={e => setSenhaAtual(e.target.value)} />
            </>}
            {(reset || first) && <>
              <label htmlFor="ello-security-new">Nova palavra-passe</label>
              <input id="ello-security-new" required type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                minLength={first ? 6 : 10} value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <label htmlFor="ello-security-confirm">Confirmar palavra-passe</label>
              <input id="ello-security-confirm" required type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              <label className="ello-security-toggle"><input type="checkbox" checked={showPassword}
                onChange={e => setShowPassword(e.target.checked)} /> Mostrar palavras-passe</label>
            </>}
            <button type="submit" disabled={working}>{working ? 'A processar…' : forgot ? 'Enviar código' : validate ? 'Validar código' : 'Guardar nova palavra-passe'}</button>
          </form>
          <button type="button" className="ello-security-back" onClick={() => navigate(loginPath, { replace: true })}>
            Voltar ao acesso ÉLLO
          </button>
        </section>
      </main>
    </div>
  );
}
